import {useMemo} from 'react';

import {LinePlotSettings} from '../../WorkspaceDrawer/Settings/types';
import {LINE_PLOT_TEMPLATE_FIELDS} from '../constants';
import {extractTemplateValues} from '../utils';
import {WorkspaceTemplate} from '../workspaceTemplatesQueries';
import {LINE_PLOT_TEMPLATE_DISPLAY_FNS} from './linePlotTemplateDisplayFns';
import {useSmoothingTypeDisplayRow} from './smoothingDisplayValues';

export const useLinePlotTemplateSections = ({
  currTemplateLinePlotSettings,
  currWorkspaceLinePlotSettings,
}: {
  currTemplateLinePlotSettings: WorkspaceTemplate['linePlotSettings'];
  currWorkspaceLinePlotSettings?: LinePlotSettings;
}) => {
  // Call the smoothingType hook at the top level (before any conditionals or loops)
  const smoothingTypeRow = useSmoothingTypeDisplayRow({
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
  });

  return useMemo(() => {
    return [
      {
        title: 'Data',
        rows: LINE_PLOT_TEMPLATE_FIELDS.map(key => {
          // Use the hook result for smoothingType
          if (key === 'smoothingType') {
            return smoothingTypeRow;
          }

          // Use regular display functions for all other fields
          const displayFn = LINE_PLOT_TEMPLATE_DISPLAY_FNS[key];
          if (!displayFn) {
            throw new Error(
              `No display function found for field: ${key}. This should not happen.`
            );
          }
          return displayFn({
            currTemplateLinePlotSettings,
            currWorkspaceLinePlotSettings,
          });
        }),
      },
    ];
  }, [
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
    smoothingTypeRow,
  ]);
};

/**
 * Extracts line plot values from a template to apply to workspace settings.
 */
export const getLinePlotTemplateValues = (
  workspaceTemplate: WorkspaceTemplate | undefined
): LinePlotSettings =>
  extractTemplateValues(
    workspaceTemplate?.linePlotSettings,
    LINE_PLOT_TEMPLATE_FIELDS
  );
